import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion, useScroll, useTransform } from "motion/react";
import { ArrowRight, ArrowUpRight } from "@phosphor-icons/react";
import { buildSteps, guides, problemCategories } from "../../data/content.js";

const statementGuide = guides.find((guide) => guide.slug === "write-a-problem-statement");
import { EASE_OUT, MaskWords, PageFrame, Photo, Seo, useMediaQuery } from "../../kit.jsx";
import { PROBLEM_EXTRA } from "./learnData.js";
import { ProblemIcon } from "./ProblemIcon.jsx";
import "./learn.css";

function AreaPreview({ active }) {
  const reduceMotion = useReducedMotion();
  const problem = problemCategories[active];
  const extra = PROBLEM_EXTRA[problem.slug];
  return (
    <div className="lb-areas__preview" aria-hidden="true">
      <div className="lb-areas__sticky">
        <div className="lb-areas__frame">
          <AnimatePresence initial={false} mode="popLayout">
            <motion.div
              key={problem.slug}
              className="lb-areas__img"
              initial={reduceMotion ? { opacity: 0 } : { clipPath: "inset(100% 0% 0% 0%)", scale: 1.12 }}
              animate={reduceMotion ? { opacity: 1 } : { clipPath: "inset(0% 0% 0% 0%)", scale: 1 }}
              exit={reduceMotion ? { opacity: 0 } : { clipPath: "inset(0% 0% 100% 0%)", scale: 1 }}
              transition={{ duration: reduceMotion ? 0.01 : 0.7, ease: [0.65, 0, 0.35, 1] }}
            >
              <Photo src={extra.photo} alt="" sizes="(max-width: 900px) 0px, 34vw" />
            </motion.div>
          </AnimatePresence>
        </div>
        <p className="lb-areas__caption">{extra.caption}</p>
        <p className="lb-areas__role"><span className="lb-mono">GRID's part</span>{problem.gridRole}</p>
      </div>
    </div>
  );
}

function AreaRows() {
  const [active, setActive] = useState(0);
  const wide = useMediaQuery("(min-width: 900px)");
  return (
    <section className="lb-areas" id="areas" aria-labelledby="lb-areas-title">
      <h2 id="lb-areas-title" className="sr-only">Five problem areas</h2>
      <ul className="lb-areas__list">
        {problemCategories.map((problem, index) => {
          const extra = PROBLEM_EXTRA[problem.slug];
          return (
            <li key={problem.slug} className={index === active ? "is-active" : undefined}>
              <Link
                to={`/problems/${problem.slug}`}
                onPointerEnter={() => setActive(index)}
                onFocus={() => setActive(index)}
              >
                <span className="lb-areas__icon"><ProblemIcon name={extra.icon} weight="regular" /></span>
                <span className="lb-areas__text">
                  <strong>{problem.title}</strong>
                  <span className="lb-areas__question">{extra.question}</span>
                </span>
                {!wide && (
                  <span className="lb-areas__thumb" aria-hidden="true">
                    <Photo src={extra.photo} alt="" sizes="96px" />
                  </span>
                )}
                <ArrowUpRight className="lb-areas__arrow" weight="bold" aria-hidden="true" />
              </Link>
            </li>
          );
        })}
      </ul>
      {wide && <AreaPreview active={active} />}
    </section>
  );
}

function ResponsibleBand() {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.8", "end 0.9"] });
  const scaleX = useTransform(scrollYProgress, (value) => Math.min(1, Math.max(0, value * 1.25)));
  return (
    <section className="lb-duty" data-nav-tone="dark" aria-labelledby="lb-duty-title">
      <div className="lb-duty__inner">
        <div className="lb-duty__head">
          <span className="lb-mono lb-mono--dark">Before you start</span>
          <MaskWords id="lb-duty-title" text="How to work on a problem responsibly." accent="responsibly." />
          <p>
            GRID connects students with context, people, tools and mentorship. The goal is not to claim a whole problem is solved. It is to help young builders make a responsible contribution. GRID is a way into the work, not a substitute for communities, experts, public systems or long-term delivery.
          </p>
        </div>
        <div className="lb-duty__steps" ref={ref}>
          <span className="lb-duty__line" aria-hidden="true">
            <motion.span style={reduceMotion ? { scaleX: 1 } : { scaleX }} />
          </span>
          <ol>
            {buildSteps.map((step) => (
              <li key={step.key}>
                <span className="lb-duty__num">{step.number}</span>
                <strong>{step.title}</strong>
                <p>{step.body}</p>
              </li>
            ))}
          </ol>
        </div>
        <ul className="lb-duty__rules">
          <li><strong>Write down three lists.</strong> What you observed, what people told you and what you are assuming. Check one assumption before you build.</li>
          <li><strong>Ask before you observe.</strong> Let people choose how to take part, and do not collect personal or sensitive information for a first project.</li>
          <li><strong>Claim only what you tested.</strong> A small, measured change is worth more than a promise to fix the whole system.</li>
        </ul>
      </div>
    </section>
  );
}

export default function ProblemsHub() {
  const reduceMotion = useReducedMotion();
  const appear = (delay) => ({
    initial: reduceMotion ? false : { opacity: 0, y: 18 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.9, delay, ease: EASE_OUT },
  });
  return (
    <PageFrame className="rd-page learn-page lb-problems">
      <Seo
        title="Problems Project GRID Helps Young People Address"
        path="/problems"
        description="See how Project GRID helps young people understand real-world challenges, test responsible responses and contribute useful work."
      />
      <main>
        <header className="lb-phub-hero">
          <motion.p className="lb-mono" {...appear(0)}>Problems to work on · {problemCategories.length} areas</motion.p>
          <MaskWords as="h1" text="Real problems you can start on." onMount delay={0.05} />
          <div className="lb-phub-hero__row">
            <motion.p {...appear(0.4)}>
              Each area below is a starting point for research, with a question you can investigate at school or near home. We help students understand a problem and test a careful response.
            </motion.p>
            <motion.div className="gh-actions" {...appear(0.5)}>
              <a className="gh-btn gh-btn--fill" href="#areas">
                <span className="roll" data-label="Choose an area"><span>Choose an area</span></span>
              </a>
              <Link className="gh-btn gh-btn--ghost" to="/guides/find-a-real-world-problem">
                <span className="roll" data-label="How to find a problem"><span>How to find a problem</span></span>
              </Link>
            </motion.div>
          </div>
        </header>

        <AreaRows />
        <ResponsibleBand />

        <section className="lb-phub-end" aria-labelledby="lb-phub-end-title">
          <MaskWords id="lb-phub-end-title" text="Found one? Write it down properly." />
          <div className="lb-phub-end__links">
            <Link to="/guides/write-a-problem-statement">
              <span className="lb-mono">Guide · {statementGuide.readTime}</span>
              <strong>Write a useful problem statement</strong>
              <ArrowRight weight="bold" aria-hidden="true" />
            </Link>
            <Link to="/guides">
              <span className="lb-mono">All guides</span>
              <strong>The field manual, from problem to pitch</strong>
              <ArrowRight weight="bold" aria-hidden="true" />
            </Link>
          </div>
        </section>
      </main>
    </PageFrame>
  );
}
